import type {
  Locale,
  ObligationActionResultContract,
  ObligationContract,
  StudentProfileContract,
} from "@chams-pay/contracts";
import { normalizeSchoolLevelLabel, operationsMessages, translate } from "@chams-pay/contracts";
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavbarBackLink,
  Page,
  Segmented,
  SegmentedButton,
} from "konsta/react";
import { useId, type ReactNode } from "react";
import { casablancaDate, initialOf, periodLabel } from "../core/format.js";
import { useObligationAction } from "../core/use-operations.js";
import { studentFormErrorBodyKey, useStudentForm } from "../core/use-students.js";
import { DatePicker } from "../ui/date-picker.js";
import { FilterSelect } from "../ui/filter-select.js";
import { Amount, Bidi } from "../ui/figures.js";
import { showMobileFeedback } from "./app-feedback.js";
import { MobileDraggableSheet, MobileDrawerHandle } from "./draggable-sheet.js";

export function MobileSurface({
  opened,
  title,
  subtitle,
  closeLabel,
  testId,
  onClose,
  children,
  closeOnBackdrop = true,
}: {
  readonly opened: boolean;
  readonly title: string;
  readonly subtitle?: string;
  readonly closeLabel: string;
  readonly testId?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly closeOnBackdrop?: boolean;
}) {
  return (
    <MobileDraggableSheet
      opened={opened}
      onClose={onClose}
      withHandle={false}
      {...(closeOnBackdrop ? { onBackdropClick: onClose } : {})}
      className="mobile-workspace-drawer w-full rounded-t-2xl"
      {...(testId ? { "data-testid": testId } : {})}
    >
      <Page className="chams-page mobile-workspace-drawer-page flex flex-col bg-canvas">
        <MobileDrawerHandle />
        <Navbar
          className="mobile-drawer-drag-zone sticky top-0 z-30"
          data-mobile-drawer-drag-zone=""
          title={title}
          {...(subtitle ? { subtitle } : {})}
          left={<NavbarBackLink component="button" text={closeLabel} onClick={onClose} />}
        />
        {children}
      </Page>
    </MobileDraggableSheet>
  );
}

/**
 * A native checkbox rather than the Konsta toggle: it carries an accessible
 * name, which assistive technology and the browser suite both rely on.
 */
function ServiceToggle({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      role="switch"
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="size-6 accent-[rgb(var(--primary))]"
    />
  );
}

function ErrorNotice({ children }: { readonly children: ReactNode }) {
  return (
    <Block
      className="!my-3 animate-shake rounded-2xl bg-destructive-soft px-4 py-3 text-sm text-destructive"
      role="status"
    >
      {children}
    </Block>
  );
}

function StudentTextField({
  label,
  type = "text",
  value,
  required = false,
  disabled = false,
  placeholder,
  dir,
  inputClassName = "",
  error,
  onBlur,
  onChange,
}: {
  readonly label: string;
  readonly type?: "text" | "tel";
  readonly value: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly placeholder?: string | undefined;
  readonly dir?: "ltr" | "rtl";
  readonly inputClassName?: string;
  readonly error?: string | undefined;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
}) {
  const errorId = useId();
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-heading text-[11px] font-semibold text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error ? errorId : undefined}
        {...(dir ? { dir } : {})}
        className={`min-h-12 w-full rounded-2xl border border-input bg-white px-3.5 text-sm text-foreground outline-none transition-colors focus:border-teal-300 focus:ring-3 focus:ring-teal-100 ${inputClassName}`}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && (
        <span id={errorId} role="alert" className="text-2xs leading-snug text-destructive">
          {error}
        </span>
      )}
    </label>
  );
}

/* ------------------------------------------------------------------ */

export function MobileStudentFormPopup({
  locale,
  csrf,
  opened,
  student,
  classOptions = [],
  onClose,
  onSaved,
  onOpenStudent,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly opened: boolean;
  readonly student?: StudentProfileContract | undefined;
  readonly classOptions?: readonly string[];
  readonly onClose: () => void;
  readonly onSaved: (profile: StudentProfileContract) => void;
  readonly onOpenStudent?: (id: string) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const form = useStudentForm({
    open: opened,
    csrf,
    student,
    classOptions,
    onSaved: (profile) => {
      onSaved(profile);
      showMobileFeedback(student ? "student-updated" : "student-created");
    },
    onClose,
  });
  const prices = form.monthlyAmountsMad;
  const computed = form.computedMonthlyMad;
  const classes = classOptions.includes(form.value.classLabel)
    ? classOptions
    : form.value.classLabel
      ? [form.value.classLabel, ...classOptions]
      : classOptions;

  return (
    <MobileSurface
      opened={opened}
      title={t(form.editing ? "sp.editStudent" : "sp.registerStudent")}
      subtitle={t(form.step === 1 ? "sp.identity" : "sp.enrollment")}
      closeLabel={t("sp.cancel")}
      testId={form.editing ? "edit-student-surface" : "register-student-surface"}
      onClose={onClose}
      closeOnBackdrop={false}
    >
      <form
        className="student-form flex flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          if (form.step === 1) {
            form.setStep(2);
            return;
          }
          void form.save();
        }}
      >
        <div className="student-form__steps flex gap-1.5 px-4 pt-3" aria-hidden="true">
          {([1, 2] as const).map((number) => (
            <span
              key={number}
              data-active={form.step >= number}
              className="h-1 flex-1 rounded-full bg-border transition-colors duration-base data-[active=true]:bg-primary"
            />
          ))}
        </div>

        {form.step === 1 ? (
          <div className="student-form__grid m-4 flex animate-fade-up flex-col gap-3 rounded-2xl bg-white p-4 shadow-e1">
            {!form.editing && form.registrationYears.length > 1 && (
              <label className="flex flex-col gap-1.5 text-start">
                <span className="font-heading text-xs font-bold text-foreground">
                  {t("students.academicYear")}
                </span>
                <FilterSelect
                  value={form.value.academicYear ?? ""}
                  className="h-11 rounded-xl bg-white"
                  options={form.registrationYears.map((year) => ({
                    value: year.academicYear,
                    label: `${t(year.role === "current" ? "sp.currentYearGroup" : "sp.nextYearGroup")} · ${year.academicYear}`,
                  }))}
                  onChange={(academicYear) => form.patch({ academicYear })}
                />
              </label>
            )}
            <StudentTextField
              label={t("sp.nameAr")}
              required
              value={form.value.nameAr}
              dir="rtl"
              onChange={(value) => form.patch({ nameAr: value })}
            />
            <StudentTextField
              label={t("sp.nameFr")}
              required
              value={form.value.nameFr}
              dir="ltr"
              onChange={(value) => form.patch({ nameFr: value })}
            />
            <StudentTextField
              label="MASSAR"
              value={form.value.massar ?? ""}
              dir="ltr"
              inputClassName="chams-figure"
              error={form.fieldErrors.massar ? t("sp.invalidMassar") : undefined}
              onBlur={() => {
                form.blurField("massar");
                void form.checkDuplicate();
              }}
              onChange={(value) => form.patch({ massar: value || null })}
            />
            <div className="flex flex-col gap-1">
              <DatePicker
                locale={locale}
                label={t("sp.birthDate")}
                showLabel
                value={form.value.birthDate}
                max={casablancaDate()}
                invalid={form.fieldErrors.birthDate}
                describedBy={form.fieldErrors.birthDate ? "mobile-birth-date-error" : undefined}
                onBlur={() => form.blurField("birthDate")}
                onChange={(value) => form.patch({ birthDate: value })}
              />
              {form.fieldErrors.birthDate && (
                <span
                  id="mobile-birth-date-error"
                  role="alert"
                  className="text-2xs leading-snug text-destructive"
                >
                  {t("sp.invalidBirthDate")}
                </span>
              )}
            </div>
            <StudentTextField
              label={t("sp.guardian")}
              required
              value={form.value.guardianName}
              onChange={(value) => form.patch({ guardianName: value })}
            />
            <StudentTextField
              label={t("sp.phone")}
              type="tel"
              required
              disabled={form.detailsBusy}
              placeholder={form.detailsBusy ? t("state.loading") : undefined}
              value={form.value.guardianPhone ?? ""}
              dir="ltr"
              inputClassName="chams-figure"
              error={form.fieldErrors.guardianPhone ? t("sp.invalidPhone") : undefined}
              onBlur={() => form.blurField("guardianPhone")}
              onChange={(value) => form.patch({ guardianPhone: value })}
            />
          </div>
        ) : (
          <>
            <div className="mx-4 mb-4 mt-6 flex flex-col gap-3">
              <span className="font-heading text-sm font-bold text-foreground">
                {t("sp.class")}
              </span>
              <FilterSelect
                ariaLabel={t("sp.class")}
                value={form.value.classLabel}
                className="h-11 rounded-xl bg-white"
                options={[
                  { value: "", label: "—" },
                  ...classes.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(classLabel) => form.patch({ classLabel })}
              />
            </div>
            {form.sectionRequired && (
              <div className="mx-4 mb-4 flex flex-col gap-3" data-testid="mobile-student-section">
                <span className="font-heading text-sm font-bold text-foreground">
                  {t("sp.section")}
                </span>
                <FilterSelect
                  ariaLabel={t("sp.section")}
                  value={form.value.sectionLabel ?? ""}
                  className="h-11 rounded-xl bg-white"
                  options={[
                    { value: "", label: "—" },
                    ...form.sectionOptions.map((option) => ({ value: option, label: option })),
                  ]}
                  onChange={(sectionLabel) =>
                    form.patch({ sectionLabel: sectionLabel ? (sectionLabel as "A" | "B") : null })
                  }
                />
              </div>
            )}
            <List strong inset className="student-form__services">
              <ListItem
                label
                title={t("sp.transport")}
                {...(prices.transport
                  ? {
                      subtitle: (
                        <>
                          <Amount value={prices.transport ?? "0"} locale={locale} />{" "}
                          {t("sp.perMonthShort")}
                        </>
                      ),
                    }
                  : {})}
                after={
                  <ServiceToggle
                    label={t("sp.transport")}
                    checked={form.value.transport}
                    onChange={(checked) => form.patch({ transport: checked })}
                  />
                }
              />
              <ListItem
                label
                title={t("sp.accompaniment")}
                {...(prices.accompaniment
                  ? {
                      subtitle: (
                        <>
                          <Amount value={prices.accompaniment ?? "0"} locale={locale} />{" "}
                          {t("sp.perMonthShort")}
                        </>
                      ),
                    }
                  : {})}
                after={
                  <ServiceToggle
                    label={t("sp.accompaniment")}
                    checked={form.value.accompaniment}
                    onChange={(checked) => form.patch({ accompaniment: checked })}
                  />
                }
              />
            </List>
            {computed !== null && (
              <Block
                strong
                inset
                className="flex items-baseline justify-between !bg-teal-50 text-primary-strong"
              >
                <span className="text-xs">{t("sp.computedMonthly")}</span>
                <strong className="font-heading text-base">
                  <Amount value={computed} locale={locale} />
                </strong>
              </Block>
            )}
          </>
        )}

        {form.duplicate && (
          <Block strong inset className="student-form__duplicate animate-shake flex flex-col gap-3">
            <strong className="font-heading text-sm">{t("sp.dupTitle")}</strong>
            <p className="text-xs leading-relaxed text-muted-foreground">{t("sp.dupBody")}</p>
            <span className="flex items-center gap-3 rounded-2xl bg-surface-2 p-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-sunken font-heading text-xs font-bold text-muted-foreground">
                {initialOf(locale === "ar" ? form.duplicate.nameAr : form.duplicate.nameFr)}
              </span>
              <span className="flex min-w-0 flex-col">
                <strong className="truncate font-heading text-xs">
                  {locale === "ar" ? form.duplicate.nameAr : form.duplicate.nameFr}
                </strong>
                <small className="truncate text-2xs text-muted-foreground">
                  <Bidi>
                    {[
                      form.duplicate.massar,
                      form.duplicate.internalReference,
                      normalizeSchoolLevelLabel(form.duplicate.currentClass),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Bidi>
                </small>
              </span>
            </span>
            <Button
              component="button"
              type="button"
              large
              rounded
              className="min-h-12"
              onClick={() => {
                const id = form.duplicate?.id;
                form.dismissDuplicate();
                onClose();
                if (id) onOpenStudent?.(id);
              }}
            >
              {t("sp.openExisting")}
            </Button>
            <Button
              component="button"
              type="button"
              large
              rounded
              outline
              className="min-h-12"
              onClick={form.dismissDuplicate}
            >
              {t("sp.saveAnyway")}
            </Button>
          </Block>
        )}

        {form.error && (
          <ErrorNotice>
            <span className="flex flex-col gap-2">
              <strong className="font-heading">
                {t(form.error === "duplicate" ? "sp.dupTitle" : "sp.saveFailTitle")}
              </strong>
              <span className="text-xs">
                {t(studentFormErrorBodyKey(form.error, form.errorCode))}
              </span>
              {form.errorCode && <Bidi className="text-2xs">{form.errorCode}</Bidi>}
            </span>
          </ErrorNotice>
        )}

        <div className="mobile-drawer-footer student-form__footer gap-2">
          {form.step === 1 ? (
            <Button
              component="button"
              type="button"
              large
              rounded
              className="min-h-12 flex-1"
              disabled={!form.identityValid}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                form.setStep(2);
              }}
            >
              {t("sp.next")}
            </Button>
          ) : (
            <>
              <Button
                component="button"
                type="submit"
                large
                rounded
                className="min-h-12 flex-1"
                disabled={form.busy || !form.enrollmentValid}
              >
                {form.busy ? t("state.loading") : t("sp.saveStudent")}
              </Button>
              <Button
                component="button"
                type="button"
                large
                rounded
                clear
                className="min-h-12 flex-1"
                onClick={() => form.setStep(1)}
              >
                {t("sp.back")}
              </Button>
            </>
          )}
        </div>
      </form>
    </MobileSurface>
  );
}

/* ------------------------------------------------------------------ */

const adjustKinds = [
  { kind: "discount", key: "sp.discount" },
  { kind: "waiver", key: "sp.waiver" },
  { kind: "adjustment", key: "sp.correction" },
] as const;

export function MobileObligationActionSheet({
  locale,
  obligation,
  csrf,
  opened,
  studentName,
  onClose,
  onSaved,
}: {
  readonly locale: Locale;
  readonly obligation: ObligationContract;
  readonly csrf: string;
  readonly opened: boolean;
  readonly studentName?: string;
  readonly onClose: () => void;
  readonly onSaved: (result: ObligationActionResultContract) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const text = operationsMessages(locale);
  const action = useObligationAction({ obligation, csrf, onSaved, onClose });
  const preview =
    action.kind === "waiver"
      ? "0"
      : /^-?\d+$/.test(action.amount.trim())
        ? (() => {
            const delta = BigInt(action.amount.trim());
            const next =
              action.kind === "discount"
                ? BigInt(obligation.remainingMad) - delta
                : BigInt(obligation.remainingMad) + delta;
            return (next < 0n ? 0n : next).toString();
          })()
        : null;

  return (
    <MobileDraggableSheet
      opened={opened}
      onClose={onClose}
      onBackdropClick={onClose}
      className="mobile-workspace-sheet adjust-fee-sheet max-h-[calc(88dvh-var(--mobile-bottom-nav-offset))] overflow-y-auto"
      data-testid="obligation-action-surface"
    >
      <form
        className="operations-form flex flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          void action.submit();
        }}
      >
        <BlockTitle className="flex items-baseline justify-between gap-2">
          <span>{t("sp.adjustFee")}</span>
          <small className="text-2xs font-normal text-muted-foreground">
            {periodLabel(obligation.period, locale)}
            {studentName ? ` · ${studentName}` : ""}
          </small>
        </BlockTitle>

        <Block className="!my-2">
          <Segmented
            strong
            className="mobile-segmented"
            role="radiogroup"
            aria-label={text.obligationActionTitle}
            data-tab-count="3"
            data-active-index={String(adjustKinds.findIndex((entry) => entry.kind === action.kind))}
          >
            {adjustKinds.map((entry) => (
              <SegmentedButton
                key={entry.kind}
                role="radio"
                aria-checked={action.kind === entry.kind}
                active={action.kind === entry.kind}
                className="min-h-12"
                onClick={() => action.setKind(entry.kind)}
              >
                {t(entry.key)}
              </SegmentedButton>
            ))}
          </Segmented>
        </Block>

        <Block strong inset className="obligation-action-context grid grid-cols-2 gap-3 text-sm">
          <span className="flex flex-col gap-1">
            <span className="chams-eyebrow">{t("sp.originalAmount")}</span>
            <strong className="font-heading text-muted-foreground">
              <Amount value={obligation.remainingMad} locale={locale} />
            </strong>
          </span>
          <span className="flex flex-col gap-1">
            <span className="chams-eyebrow text-primary-strong/70">{t("sp.newRemaining")}</span>
            <strong className="font-heading text-primary-strong">
              {preview === null ? "—" : <Amount value={preview} locale={locale} />}
            </strong>
          </span>
        </Block>

        <List strong inset>
          {action.kind !== "waiver" && (
            <ListInput
              outline
              label={t("sp.adjustAmount")}
              type="number"
              step="1"
              inputMode="numeric"
              required
              value={action.amount}
              inputClassName="chams-figure"
              onChange={(event) => action.setAmount(event.target.value)}
            />
          )}
          <DatePicker
            locale={locale}
            label={t("sp.effectiveDate")}
            showLabel
            className="px-4 py-2.5"
            max={casablancaDate()}
            value={action.effectiveDate}
            onChange={action.setEffectiveDate}
          />
          <ListInput
            outline
            label={`${t("sp.reason")} *`}
            type="textarea"
            required
            minLength={3}
            maxLength={500}
            value={action.reason}
            onChange={(event) => action.setReason(event.target.value)}
          />
        </List>

        <Block strong inset className="!bg-warning-soft text-xs leading-relaxed !text-warning">
          {t("sp.adjustWarn")}
        </Block>

        {action.failed && <ErrorNotice>{text.actionFailed}</ErrorNotice>}

        <div className="mobile-drawer-footer operations-surface-actions gap-2">
          <Button
            component="button"
            type="submit"
            large
            rounded
            className="min-h-12 flex-1"
            disabled={action.submitDisabled}
          >
            {t("sp.applyAdjust")}
          </Button>
        </div>
      </form>
    </MobileDraggableSheet>
  );
}
